/**
 * Sim-lab exterior environment catalog — assets + presets.
 * Docs: docs/SIM_LAB_EXTERIOR_ENV.md
 *
 * status:
 *   candidate — shortlisted; download/pack before runtime use
 *   local     — packed; served from exteriorBaseUrl() (dev disk / prod Blob)
 *   blob      — same as local (kept for clarity in curator notes)
 *   missing   — known gap
 */

/** @typedef {'sky' | 'ground' | 'water' | 'foliage' | 'props'} ExteriorRole */
/** @typedef {'candidate' | 'local' | 'blob' | 'missing'} ExteriorAssetStatus */

/**
 * @typedef {Object} ExteriorAsset
 * @property {string} id
 * @property {ExteriorRole} role
 * @property {ExteriorAssetStatus} status
 * @property {string} license
 * @property {string} attribution
 * @property {string} sourceUrl
 * @property {string | null} url
 * @property {'hdr' | 'glb' | 'texture-set' | 'procedural'} format
 * @property {{ diffuse?: string, normal?: string, rough?: string }} [maps]
 * @property {string} [label] - short variant chip label (sky variants)
 * @property {string} [notes]
 */

/**
 * Public Vercel Blob prefix for lab exterior packs (same store as models —
 * docs/MODEL_ASSETS.md). public/lab-exterior/* is gitignored, so production
 * must never fall back to same-origin paths (SPA rewrite returns index.html,
 * which crashes RGBELoader and takes the WebGL context down with it).
 */
export const DEFAULT_LAB_EXTERIOR_BLOB_BASE =
  'https://ekqciisicrfqj7n1.public.blob.vercel-storage.com/lab-exterior';

/** Trailing-slash-safe base for exterior assets (dev disk / prod Blob). */
export function exteriorBaseUrl() {
  const raw = (process.env.REACT_APP_LAB_EXTERIOR_BASE_URL || '').trim();
  if (raw) return raw.replace(/\/+$/, '');
  if (process.env.NODE_ENV === 'production') {
    return DEFAULT_LAB_EXTERIOR_BLOB_BASE;
  }
  return '/lab-exterior';
}

const BEACH_BASE = `${exteriorBaseUrl()}/beach`;

/**
 * @typedef {Object} ExteriorEnv
 * @property {string} id
 * @property {string} label
 * @property {string} description
 * @property {number} clearanceMarginM
 * @property {number} groundRadiusM
 * @property {Record<ExteriorRole, { assetId: string | null, required: boolean }>} layers
 */

/** @type {ExteriorAsset[]} */
export const LAB_EXTERIOR_ASSETS = [
  // —— Beach ——
  {
    id: 'beach-sky',
    role: 'sky',
    status: 'local',
    license: 'CC0',
    attribution: 'Poly Haven — Umhlanga Sunrise',
    sourceUrl: 'https://polyhaven.com/a/umhlanga_sunrise',
    url: `${BEACH_BASE}/umhlanga_sunrise_4k.hdr`,
    format: 'hdr',
    label: 'sunrise',
    notes:
      'Default beach sky — open sand + waves, no close-up people. 4K ground-projected.',
  },
  {
    id: 'beach-sky-secluded',
    role: 'sky',
    status: 'local',
    license: 'CC0',
    attribution: 'Poly Haven — Secluded Beach',
    sourceUrl: 'https://polyhaven.com/a/secluded_beach',
    url: `${BEACH_BASE}/secluded_beach_4k.hdr`,
    format: 'hdr',
    label: 'cove',
    notes: 'Empty cove with rocks/trees — midday variant.',
  },
  {
    id: 'beach-sky-fishhoek',
    role: 'sky',
    status: 'local',
    license: 'CC0',
    attribution: 'Poly Haven — Fish Hoek Beach (Greg Zaal / Rico Cilliers)',
    sourceUrl: 'https://polyhaven.com/a/fish_hoek_beach',
    url: `${BEACH_BASE}/fish_hoek_beach_4k.hdr`,
    format: 'hdr',
    label: 'overcast',
    notes:
      'Overcast variant. Caveat: photographer near tripod projects large ("giant") — kept for lighting variety.',
  },
  {
    id: 'beach-ground',
    role: 'ground',
    status: 'local',
    license: 'CC0',
    attribution: 'Poly Haven — Aerial Beach 01',
    sourceUrl: 'https://polyhaven.com/a/aerial_beach_01',
    url: `${BEACH_BASE}/aerial_beach_01_diff_2k.jpg`,
    format: 'texture-set',
    maps: {
      diffuse: `${BEACH_BASE}/aerial_beach_01_diff_2k.jpg`,
      normal: `${BEACH_BASE}/aerial_beach_01_nor_gl_2k.jpg`,
      rough: `${BEACH_BASE}/aerial_beach_01_rough_2k.jpg`,
    },
    notes: 'Tiled sand disk ~80 m. Phase 1 packed 2K JPG.',
  },
  {
    id: 'beach-water',
    role: 'water',
    status: 'candidate',
    license: 'CC0',
    attribution: 'Real water mesh TBD (Phase 2+)',
    sourceUrl: 'https://ambientcg.com/list?q=water',
    url: null,
    format: 'procedural',
    notes:
      'Phase 1 uses the sea baked into the ground-projected HDRI. Revisit for animated water.',
  },
  {
    id: 'beach-foliage',
    role: 'foliage',
    status: 'candidate',
    license: 'CC0',
    attribution: 'Poly Haven — Smuggler\'s Cove / dune grass (TBD pick)',
    sourceUrl: 'https://polyhaven.com/collections/smugglers_cove',
    url: null,
    format: 'glb',
    notes: 'Phase 2 — sparse palms / dune grass instances.',
  },

  // —— Jungle ——
  {
    id: 'jungle-sky',
    role: 'sky',
    status: 'candidate',
    license: 'CC0',
    attribution: 'Poly Haven — Rainforest Trail',
    sourceUrl: 'https://polyhaven.com/a/rainforest_trail',
    url: null,
    format: 'hdr',
    notes: 'Canopy feel through windows. Prefer 4K.',
  },
  {
    id: 'jungle-sky-alt',
    role: 'sky',
    status: 'candidate',
    license: 'CC0',
    attribution: 'Poly Haven — Nature Reserve Forest',
    sourceUrl: 'https://polyhaven.com/a/nature_reserve_forest',
    url: null,
    format: 'hdr',
    notes: 'Softer forest backup.',
  },
  {
    id: 'jungle-ground',
    role: 'ground',
    status: 'candidate',
    license: 'CC0',
    attribution: 'Poly Haven / ambientCG forest floor',
    sourceUrl: 'https://polyhaven.com/textures',
    url: null,
    format: 'texture-set',
    notes: 'Dirt + moss / leaf litter tile.',
  },
  {
    id: 'jungle-foliage',
    role: 'foliage',
    status: 'candidate',
    license: 'CC0',
    attribution: 'Poly Haven — Pine Forest collection',
    sourceUrl: 'https://polyhaven.com/collections/pine_forest',
    url: null,
    format: 'glb',
    notes: 'Phase 2 — instanced trees/grass outside clearance.',
  },

  // —— Mountains ——
  {
    id: 'mountains-sky',
    role: 'sky',
    status: 'candidate',
    license: 'CC0',
    attribution: 'Poly Haven — Golden Gate Hills',
    sourceUrl: 'https://polyhaven.com/a/golden_gate_hills',
    url: null,
    format: 'hdr',
    notes: 'Open hills / mountain horizon.',
  },
  {
    id: 'mountains-ground',
    role: 'ground',
    status: 'candidate',
    license: 'CC0',
    attribution: 'Poly Haven — Rocky Terrain (+ grass patch)',
    sourceUrl: 'https://polyhaven.com/a/rocky_terrain',
    url: null,
    format: 'texture-set',
    notes: 'Rocky / alpine ground tile.',
  },
  {
    id: 'mountains-water',
    role: 'water',
    status: 'candidate',
    license: 'CC0',
    attribution: 'Procedural alpine lake plane',
    sourceUrl: 'https://ambientcg.com/list?q=water',
    url: null,
    format: 'texture-set',
    notes: 'Optional distant lake; Phase 2.',
  },
  {
    id: 'mountains-foliage',
    role: 'foliage',
    status: 'candidate',
    license: 'CC0',
    attribution: 'Poly Haven — rocks + conifers (Pine Forest / Namaqualand rocks)',
    sourceUrl: 'https://polyhaven.com/collections/pine_forest',
    url: null,
    format: 'glb',
    notes: 'Phase 2 — sparse rocks / pines.',
  },
];

/** @type {ExteriorEnv[]} */
export const LAB_EXTERIOR_ENVS = [
  {
    id: 'none',
    label: 'None (lab)',
    description: 'Warehouse IBL + grid pad — current default',
    clearanceMarginM: 0,
    groundRadiusM: 0,
    layers: {
      sky: { assetId: null, required: false },
      ground: { assetId: null, required: false },
      water: { assetId: null, required: false },
      foliage: { assetId: null, required: false },
      props: { assetId: null, required: false },
    },
  },
  {
    id: 'beach',
    label: 'Beach',
    description: 'Coastal sand + ocean horizon',
    clearanceMarginM: 2,
    groundRadiusM: 80,
    layers: {
      sky: {
        assetId: 'beach-sky',
        required: true,
        variantAssetIds: [
          'beach-sky',
          'beach-sky-secluded',
          'beach-sky-fishhoek',
        ],
      },
      ground: { assetId: 'beach-ground', required: true },
      water: { assetId: 'beach-water', required: false },
      foliage: { assetId: 'beach-foliage', required: false },
      props: { assetId: null, required: false },
    },
  },
  {
    id: 'jungle',
    label: 'Jungle',
    description: 'Lush canopy / forest floor',
    clearanceMarginM: 2.5,
    groundRadiusM: 70,
    layers: {
      sky: { assetId: 'jungle-sky', required: true },
      ground: { assetId: 'jungle-ground', required: true },
      water: { assetId: null, required: false },
      foliage: { assetId: 'jungle-foliage', required: false },
      props: { assetId: null, required: false },
    },
  },
  {
    id: 'mountains',
    label: 'Mountains',
    description: 'Open hills / alpine ground',
    clearanceMarginM: 2,
    groundRadiusM: 90,
    layers: {
      sky: { assetId: 'mountains-sky', required: true },
      ground: { assetId: 'mountains-ground', required: true },
      water: { assetId: 'mountains-water', required: false },
      foliage: { assetId: 'mountains-foliage', required: false },
      props: { assetId: null, required: false },
    },
  },
];

const assetById = new Map(LAB_EXTERIOR_ASSETS.map((a) => [a.id, a]));
const envById = new Map(LAB_EXTERIOR_ENVS.map((e) => [e.id, e]));

export function getExteriorEnv(id) {
  return envById.get(id) || envById.get('none');
}

export function getExteriorAsset(id) {
  if (!id) return null;
  return assetById.get(id) || null;
}

/** True when an asset can be used at runtime. */
export function exteriorAssetIsUsable(asset) {
  if (!asset) return false;
  if (asset.status !== 'local' && asset.status !== 'blob') return false;
  if (asset.format === 'procedural') return true;
  return Boolean(asset.url);
}

/** True when required layers for this env are packed (local/blob + url). */
export function exteriorEnvIsReady(envId) {
  const env = getExteriorEnv(envId);
  if (!env || env.id === 'none') return true;
  return Object.values(env.layers).every((slot) => {
    if (!slot.required || !slot.assetId) return true;
    return exteriorAssetIsUsable(getExteriorAsset(slot.assetId));
  });
}

/** Usable sky variants for a preset (order = catalog order). */
export function listExteriorSkyVariants(envId) {
  const env = getExteriorEnv(envId);
  const ids = env?.layers?.sky?.variantAssetIds || [];
  return ids
    .map((id) => getExteriorAsset(id))
    .filter(exteriorAssetIsUsable)
    .map((a) => ({ id: a.id, label: a.label || a.id, attribution: a.attribution }));
}

/**
 * Resolve layer assets for a preset (only usable rows).
 * @param {string} envId
 * @param {{ skyAssetId?: string | null }} [overrides] e.g. sky variant pick
 */
export function resolveExteriorPack(envId, overrides = {}) {
  const env = getExteriorEnv(envId);
  if (!env || env.id === 'none') return null;
  const pack = {
    env,
    sky: null,
    ground: null,
    water: null,
    foliage: null,
    props: null,
  };
  for (const role of Object.keys(env.layers)) {
    const slot = env.layers[role];
    if (!slot?.assetId) continue;
    const asset = getExteriorAsset(slot.assetId);
    if (exteriorAssetIsUsable(asset)) pack[role] = asset;
  }
  if (overrides.skyAssetId) {
    const variantIds = env.layers.sky?.variantAssetIds || [];
    if (variantIds.includes(overrides.skyAssetId)) {
      const skyOverride = getExteriorAsset(overrides.skyAssetId);
      if (exteriorAssetIsUsable(skyOverride)) pack.sky = skyOverride;
    }
  }
  if (!pack.sky || !pack.ground) return null;
  return pack;
}

export function listExteriorEnvOptions() {
  return LAB_EXTERIOR_ENVS.map((e) => ({
    id: e.id,
    label: e.label,
    description: e.description,
    ready: exteriorEnvIsReady(e.id),
  }));
}

/** Curator helper — candidates still needing download/pack. */
export function listExteriorCandidates(role) {
  return LAB_EXTERIOR_ASSETS.filter(
    (a) => a.status === 'candidate' && (!role || a.role === role)
  );
}
